import { Supplies } from "@highmarch/app";
import type { SupplyId } from "@highmarch/domain";
import { priceButtonHtml, secHtml, slotTileHtml } from "../components";
import { fmtCompact } from "../kit";
import { toast } from "../toast";
import { econCue, purchasePop, tickPill } from "../hub/purchaseFx";
import { goldIcon } from "../hub/econArt";
import { openItemPopup } from "../shops/itemPopup";
import { itemCardHtml } from "../shops/itemCard";
import { refusalWords } from "../hub/refusalWords";
import type { ShopDeps } from "../hub/shopSheet";
import { supplyIconHtml, supplyIconUrl } from "./supplyArt";
import { notEnoughGold } from "../hub/getGold";

/**
 * The Shop's Supplies (MP-06, docs/SHOP.md §14): the Gems page's "Gems buy" list gains the four pre-battle supplies, as
 * slot tiles like the speed-ups and gold packs beside them — the item in a navy well with how many are held, what it
 * does, and its gold price button (gold since 2026-10-23, gems before). A buy confirms (what it does, the price), spends once through @highmarch/app Supplies
 * (hub only), then ticks the gem pill and says where the item waits (the battle card). Mounted by shopSheet.ts.
 */

/** Section and confirm copy. */
const SEC_NOTE = "";
const WHERE = "It waits on the battle card: tap it there before a battle.";

export function suppliesShelfHtml(s: Supplies): string {
  if (!s.shopOpen) return "";
  const tiles = s.slots().map(({ item, have }) => {
    const cost = s.goldPrice(item);
    const price = priceButtonHtml({ currency: "gold", amount: fmtCompact(cost), short: goldOf() < cost, className: "ebuy b-supply", data: `data-sup="${item.id}"` });
    return slotTileHtml({
      well: { art: supplyIconUrl(item.id), count: have > 1 ? `×${have}` : "" }, name: item.name, left: "", price,   // the confirm says what it does (owner 2026-10-19: text to a minimum)
      className: "pack supply", data: `data-sup="${item.id}"`,
    });
  }).join("");
  return `<section class="ssec packs supplies">${secHtml("Supplies", SEC_NOTE, supplyIconHtml("warBanner", 20))}<div class="k-grid3 pgrid n2 supgrid">${tiles}</div></section>`;
}

/** The gold held, for the price buttons' short state (set by the mounted shelf). */
let goldOf: () => number = () => 0;

/** Draws the shelf at the end of `container` (the Gems page's goods) and binds its buttons; redraws in place after a buy. */
export function mountSuppliesShelf(container: HTMLElement, d: ShopDeps): void {
  const s = new Supplies(d.catalog);
  goldOf = () => d.catalog.profile.gold;
  // a slot of its own, so a redraw after a buy keeps its place (the Skins shelf is appended after it)
  const slot = document.createElement("div");
  slot.className = "supslot";
  container.append(slot);
  const draw = (): void => {
    slot.innerHTML = suppliesShelfHtml(s);
    // the whole tile opens the item popup (its price button's tap bubbles to the tile)
    for (const t of slot.querySelectorAll<HTMLElement>(".supply[data-sup]")) t.onclick = () => void buy(t.dataset.sup as SupplyId, t);
  };
  const buy = async (id: SupplyId, card: Element | null): Promise<void> => {
    const item = s.data.items.find((x) => x.id === id);
    if (!item) return;
    const parent = d.hub.parent, profile = d.catalog.profile, cost = s.goldPrice(item);
    const have = s.slots().find((x) => x.item.id === id)?.have ?? 0;
    const ok = await openItemPopup(parent, { title: "SUPPLIES", price: `${goldIcon(20)} ${fmtCompact(cost)}`, className: "c-gold",
      body: itemCardHtml({ well: { art: supplyIconUrl(id), count: have > 1 ? `×${have}` : "" }, name: item.name, rarity: null, what: `${item.blurb}. ${WHERE}` }) });
    if (!ok) return;
    // owner 2026-10-23: supplies are bought for gold
    if (profile.gold < cost) { void notEnoughGold(parent, cost - profile.gold, slot.closest(".sheet")); return; }
    const before = profile.gold;
    const r = s.buy(id, d.now(), "hub");
    if (r !== true) { toast(parent, refusalWords(r)); draw(); return; }
    econCue("buy");
    purchasePop(card);
    d.hub.changed();
    tickPill("gold", before, profile.gold);
    toast(parent, `+1 ${item.name}: take it from the battle card`);
    draw();
  };
  draw();
}
